import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Input, Modal, Tooltip } from 'antd';
import {
    DownOutlined,
    ExclamationCircleFilled,
    ExclamationCircleOutlined,
    FileAddOutlined,
    MoreOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faThLarge } from '@fortawesome/free-solid-svg-icons';
import AddServiceItemModal from './ServiceItemModal';
import AppMetadataModal from './AppMetadataModal';
import ApplicationYAMLModal from './ApplicationYAMLModal';
import { ServiceItem } from '@src/types';

const { confirm } = Modal;

interface ApplicationPanelProps {
    application: string;
    appDeclarationData: any;
    selectedServiceItem: ServiceItem | null;
    selectedService: string | null;
    setSelectedService: Function;
    newApplicationsCreated: string[];
    onAddServiceItem: (serviceName: string, serviceItemName: string) => void;
    onEditAppMetadata: (appName: string, metadata: object) => void;
    /** a service item was chosen: which service, and which item of it */
    onSelectServiceItem: (serviceName: string, serviceItemName: string) => void;
    serviceItemsWithErrors: ServiceItem[];
    onRenameApp: (previousName: string, newName: string) => void;
    onRemoveApp?: (applicationName: string) => void;
    isActive?: boolean;
    onToggle?: (application: string) => void;
}

/** `{ VM: [{name: 'vm1'}, ...] }` → `[['VM', [...]], ...]`, services in name order */
const readServices = (appDeclarationData: any): [string, any[]][] =>
    Object.entries(appDeclarationData?.services ?? {})
        .filter((entry): entry is [string, any[]] => Array.isArray(entry[1]))
        .sort(([a], [b]) => a.localeCompare(b));

/**
 * One application in the declaration: a disclosure holding its service items,
 * grouped by service.
 *
 * This was an antd `Collapse` per application — so there was no single
 * accordion, just a stack of independent ones — wrapped around an antd `Tree`
 * whose node keys packed the service and item names into one string separated
 * by a unit separator, which the editor above then split apart again. Plain
 * buttons in a nested list say the same thing, hand the two names over as two
 * arguments, and can be reached with a keyboard.
 */
const ApplicationPanel = ({
    application,
    appDeclarationData,
    selectedServiceItem,
    selectedService,
    setSelectedService,
    onAddServiceItem,
    onSelectServiceItem,
    onEditAppMetadata,
    onRemoveApp,
    onRenameApp,
    serviceItemsWithErrors,
    newApplicationsCreated,
    isActive,
    onToggle,
}: ApplicationPanelProps) => {
    const [isServiceItemModalOpen, setIsServiceItemModalOpen] = useState<boolean>(false);
    const [isAppMetadataModalOpen, setIsAppMetadataModalOpen] = useState<boolean>(false);
    const [isAppYamlModalOpen, setIsAppYamlModalOpen] = useState<boolean>(false);
    const [isEditingAppTitle, setIsEditingAppTitle] = useState<boolean>(false);
    const [expandedServices, setExpandedServices] = useState<string[]>([]);

    const services = useMemo(() => readServices(appDeclarationData), [appDeclarationData]);
    const itemCount = useMemo(
        () => services.reduce((total, [, items]) => total + items.length, 0),
        [services],
    );
    /* Names are unique per service, not per application: two services may each
       have an item called `primary`, and the declaration addresses them by
       both names together. */
    const existingNamesByService = useMemo(
        () => Object.fromEntries(
            services.map(([serviceName, items]) => [serviceName, items.map((item: any) => String(item.name))]),
        ),
        [services],
    );

    // selecting an item in the editor opens the group it belongs to
    useEffect(() => {
        if (selectedService) {
            setExpandedServices((previous) =>
                previous.includes(selectedService) ? previous : [...previous, selectedService],
            );
        }
    }, [selectedService]);

    /* ids rather than a shared string: `aria-controls` has to point at this
       application's panel, and there is one panel per application on screen */
    const bodyId = `sb-app-body-${application}`;
    const groupId = (serviceName: string) => `sb-svc-body-${application}-${serviceName}`;

    const appHasErrors = serviceItemsWithErrors.some((item) => item.applicationName === application);
    const itemHasError = (serviceItemName: string) =>
        serviceItemsWithErrors.some(
            (item) => item.applicationName === application && item.name === serviceItemName,
        );

    const handleDelete = () => {
        confirm({
            icon: <ExclamationCircleOutlined />,
            title: `Remove ${application}?`,
            content: 'Its service items are removed from the declaration with it.',
            okText: 'Remove',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                onRemoveApp?.(application);
            },
        });
    };

    const appContextMenuItems = [
        { key: 'yaml', label: 'View app YAML', onClick: () => setIsAppYamlModalOpen(true) },
        { key: 'rename', label: 'Rename application', onClick: () => setIsEditingAppTitle(true) },
        { key: 'metadata', label: 'Edit metadata', onClick: () => setIsAppMetadataModalOpen(true) },
        { key: 'remove', label: 'Remove application', danger: true, onClick: () => handleDelete() },
    ];

    const toggleService = (serviceName: string) => {
        setSelectedService(serviceName);
        setExpandedServices((previous) =>
            previous.includes(serviceName)
                ? previous.filter((name) => name !== serviceName)
                : [...previous, serviceName],
        );
    };

    const commitRename = (nextName: string) => {
        setIsEditingAppTitle(false);
        const trimmed = nextName.trim();
        if (trimmed && trimmed !== application) onRenameApp(application, trimmed);
    };

    /* Renaming an application that has already been submitted deletes and
       recreates its service items, which is not recoverable from here. A new
       application has nothing to lose, so it is not warned about. */
    const isNewApp = newApplicationsCreated.includes(application);

    const renderTitle = () => {
        if (isEditingAppTitle) {
            return (
                <Tooltip
                    color="orange"
                    open={!isNewApp}
                    title="Renaming an existing application deletes and recreates all of its service items"
                >
                    <Input
                        defaultValue={application}
                        autoFocus
                        aria-label={`Rename ${application}`}
                        size="small"
                        onBlur={(e) => commitRename(e.target.value)}
                        onPressEnter={(e) => commitRename((e.target as HTMLInputElement).value)}
                        onKeyDown={(e) => {
                            // leaving the name as it was has to be possible
                            // without committing whatever is currently typed
                            if (e.key === 'Escape') {
                                e.stopPropagation();
                                setIsEditingAppTitle(false);
                            }
                        }}
                    />
                </Tooltip>
            );
        }

        return (
            <button
                type="button"
                className="sb-app__toggle"
                aria-expanded={Boolean(isActive)}
                aria-controls={bodyId}
                onClick={() => onToggle?.(application)}
            >
                <span className="sb-app__chev" aria-hidden="true">
                    {isActive ? <DownOutlined /> : <RightOutlined />}
                </span>
                {/* the same glyph applications carry everywhere else in the app
                    — the lists, the search results, the nav */}
                <span className="sb-app__ico" aria-hidden="true">
                    <FontAwesomeIcon icon={faThLarge} />
                </span>
                <span className="sb-app__name">{application}</span>
                {appHasErrors && (
                    <>
                        <ExclamationCircleFilled className="sb-app__bad" aria-hidden="true" />
                        {/* the red is not the message; this is */}
                        <span className="sb-sr">has validation errors</span>
                    </>
                )}
                <span className="sb-app__count">{itemCount}</span>
            </button>
        );
    };

    return (
        <section className={`sb-app${isActive ? ' sb-app--open' : ''}${appHasErrors ? ' sb-app--bad' : ''}`}>
            <div className="sb-app__head">
                {renderTitle()}
                <Dropdown menu={{ items: appContextMenuItems }} trigger={['click']}>
                    {/* was a bare icon: nothing to tab to, and nothing said what
                        it would act on once you got there */}
                    <Button
                        type="text"
                        size="small"
                        className="sb-app__menu"
                        icon={<MoreOutlined />}
                        aria-label={`Actions for ${application}`}
                    />
                </Dropdown>
            </div>

            {/* `hidden` rather than unmounting: a collapsed application keeps
                its state, as it did under `forceRender` */}
            <div id={bodyId} className="sb-app__body" hidden={!isActive}>
                {services.length === 0 ? (
                    <p className="sb-app__empty">No service items yet.</p>
                ) : (
                    <ul className="sb-svcs">
                        {services.map(([serviceName, items]) => {
                            const isExpanded = expandedServices.includes(serviceName);
                            return (
                                <li key={serviceName} className="sb-svc">
                                    <button
                                        type="button"
                                        className="sb-svc__toggle"
                                        aria-expanded={isExpanded}
                                        aria-controls={groupId(serviceName)}
                                        onClick={() => toggleService(serviceName)}
                                    >
                                        <span className="sb-svc__chev" aria-hidden="true">
                                            {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                        </span>
                                        <span className="sb-svc__name">{serviceName}</span>
                                        <span className="sb-svc__count">{items.length}</span>
                                    </button>

                                    <ul
                                        id={groupId(serviceName)}
                                        className="sb-items"
                                        hidden={!isExpanded}
                                    >
                                        {items.map((item: any) => {
                                            const name = String(item.name);
                                            const isSelected =
                                                selectedServiceItem?.serviceName === serviceName &&
                                                selectedServiceItem?.name === name;
                                            const isBad = itemHasError(name);
                                            return (
                                                <li key={name}>
                                                    <button
                                                        type="button"
                                                        className={`sb-item${isSelected ? ' sb-item--on' : ''}${isBad ? ' sb-item--bad' : ''}`}
                                                        aria-current={isSelected ? 'true' : undefined}
                                                        onClick={() => onSelectServiceItem(serviceName, name)}
                                                    >
                                                        <span className="sb-item__ico" aria-hidden="true">
                                                            <FontAwesomeIcon icon={faRocket} />
                                                        </span>
                                                        <span className="sb-item__name">{name}</span>
                                                        {isBad && (
                                                            <>
                                                                <ExclamationCircleFilled aria-hidden="true" />
                                                                <span className="sb-sr">has validation errors</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </li>
                            );
                        })}
                    </ul>
                )}

                <Button
                    type="dashed"
                    block
                    icon={<FileAddOutlined />}
                    className="sb-app__add"
                    onClick={() => setIsServiceItemModalOpen(true)}
                >
                    New Service Item
                </Button>

                {isServiceItemModalOpen && (
                    <AddServiceItemModal
                        handleModalSubmit={onAddServiceItem}
                        existingNamesByService={existingNamesByService}
                        modalVisible={isServiceItemModalOpen}
                        setModalVisible={setIsServiceItemModalOpen}
                    />
                )}
                {isAppMetadataModalOpen && (
                    <AppMetadataModal
                        appName={application}
                        metadata={appDeclarationData?.metadata}
                        handleModalSubmit={onEditAppMetadata}
                        modalVisible={isAppMetadataModalOpen}
                        setModalVisible={setIsAppMetadataModalOpen}
                    />
                )}
                {isAppYamlModalOpen && (
                    <ApplicationYAMLModal
                        appName={application}
                        appDeclarationData={appDeclarationData}
                        modalVisible={isAppYamlModalOpen}
                        setModalVisible={setIsAppYamlModalOpen}
                    />
                )}
            </div>
        </section>
    );
};

export default ApplicationPanel;
